import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  FloorPlan,
  CreateFloorPlanInput,
  UpdateFloorPlanInput,
  AssignGuestsToTableInput,
  TableAssignment,
  Room,
  CreateRoomInput,
  UpdateRoomInput,
  CreateRoomEntryInput,
  CreateFloorPlanObjectInput,
  UpdateFloorPlanObjectInput,
  BulkUpsertObjectsInput,
  DuplicateFloorPlanInput,
} from '@peles/shared';
import * as floorPlansApi from '../api/floor-plans';

export function useFloorPlans(filters?: {
  eventId?: string;
  venueId?: string;
  isTemplate?: boolean;
}) {
  return useQuery<FloorPlan[]>({
    queryKey: ['floor-plans', filters ?? 'all'],
    queryFn: () => floorPlansApi.getFloorPlans(filters),
  });
}

export function useFloorPlan(id?: string) {
  return useQuery<FloorPlan>({
    queryKey: ['floor-plans', id],
    queryFn: () => floorPlansApi.getFloorPlan(id!),
    enabled: !!id,
  });
}

export function useTableAssignments(eventId?: string) {
  return useQuery<Record<number, TableAssignment['guests']>>({
    queryKey: ['floor-plans', 'assignments', eventId],
    queryFn: () => floorPlansApi.getTableAssignments(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateFloorPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFloorPlanInput) => floorPlansApi.createFloorPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useUpdateFloorPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateFloorPlanInput }) =>
      floorPlansApi.updateFloorPlan(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useDeleteFloorPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => floorPlansApi.deleteFloorPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useAssignGuestsToTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignGuestsToTableInput) => floorPlansApi.assignGuestsToTable(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useCloneAsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, templateName }: { id: string; templateName?: string }) =>
      floorPlansApi.cloneAsTemplate(id, templateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useApplyTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      eventId,
      name,
    }: {
      templateId: string;
      eventId: string;
      name?: string;
    }) => floorPlansApi.applyTemplate(templateId, eventId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

// ─── Rooms ──────────────────────────────────────────────────────────────────

export function useRooms(venueId?: string, enabled = true) {
  return useQuery<Room[]>({
    queryKey: ['rooms', venueId ?? 'all'],
    queryFn: () => floorPlansApi.getRooms(venueId),
    enabled,
  });
}

export function useCreateRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoomInput) => floorPlansApi.createRoom(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

export function useUpdateRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateRoomInput }) =>
      floorPlansApi.updateRoom(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

export function useDeleteRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => floorPlansApi.deleteRoom(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

export function useCreateRoomEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, input }: { roomId: string; input: CreateRoomEntryInput }) =>
      floorPlansApi.createRoomEntry(roomId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

export function useDeleteRoomEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, entryId }: { roomId: string; entryId: string }) =>
      floorPlansApi.deleteRoomEntry(roomId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

// ─── Floor Plan Objects ─────────────────────────────────────────────────────

export function useCreateFloorPlanObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      floorPlanId,
      input,
    }: {
      floorPlanId: string;
      input: CreateFloorPlanObjectInput;
    }) => floorPlansApi.createFloorPlanObject(floorPlanId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useUpdateFloorPlanObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      floorPlanId,
      objId,
      updates,
    }: {
      floorPlanId: string;
      objId: string;
      updates: UpdateFloorPlanObjectInput;
    }) => floorPlansApi.updateFloorPlanObject(floorPlanId, objId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useDeleteFloorPlanObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ floorPlanId, objId }: { floorPlanId: string; objId: string }) =>
      floorPlansApi.deleteFloorPlanObject(floorPlanId, objId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

export function useBulkUpsertObjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ floorPlanId, input }: { floorPlanId: string; input: BulkUpsertObjectsInput }) =>
      floorPlansApi.bulkUpsertObjects(floorPlanId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

// ─── Duplicate ──────────────────────────────────────────────────────────────

export function useDuplicateFloorPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DuplicateFloorPlanInput }) =>
      floorPlansApi.duplicateFloorPlan(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    },
  });
}

// ─── Share ───────────────────────────────────────────────────────────────────

export function useShareFloorPlan() {
  return useMutation({
    mutationFn: (id: string) => floorPlansApi.shareFloorPlan(id),
  });
}

// ─── Seat Assignment ─────────────────────────────────────────────────────────

export function useAssignGuestToSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guestId,
      tableNumber,
      seatNumber,
    }: {
      guestId: string;
      tableNumber: number;
      seatNumber: number;
    }) => floorPlansApi.assignGuestToSeat(guestId, tableNumber, seatNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

// ─── Table Capacities ────────────────────────────────────────────────────────

export function useEventTableCapacities(eventId?: string) {
  return useQuery<Record<number, number>>({
    queryKey: ['floor-plans', 'table-capacities', eventId],
    queryFn: () => floorPlansApi.getTableCapacities(eventId!),
    enabled: !!eventId,
  });
}

// ─── Meal Counts ─────────────────────────────────────────────────────────────

export function useTableMealCounts(eventId?: string) {
  return useQuery<Record<number, Record<string, number>>>({
    queryKey: ['floor-plans', 'meal-counts', eventId],
    queryFn: () => floorPlansApi.getTableMealCounts(eventId!),
    enabled: !!eventId,
  });
}

// ─── Auto-Assign Seats ───────────────────────────────────────────────────────

export function useAutoAssignSeats(floorPlanId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    floorPlansApi.AutoAssignSeatsResult,
    Error,
    floorPlansApi.AutoAssignSeatsRequest
  >({
    mutationFn: (body) => floorPlansApi.autoAssignSeats(floorPlanId!, body),
    onSuccess: (result) => {
      // Only invalidate when the call actually persisted, otherwise a dry-run
      // preview would needlessly refetch the floor-plan and guest lists.
      if (result.applied) {
        queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
        queryClient.invalidateQueries({ queryKey: ['guests'] });
      }
    },
  });
}
